import { useGetBookmarkedResources } from '../../query/learning-api/use-resource';
import { LibraryFilterState } from '../../utils';
import { BookmarkedResources } from '../../../services/learning-api';
import { isInternalBool } from '../../../utils/library-helper';
import { useEffect, useMemo } from 'react';
import { logEvent } from 'firebase/analytics';
import { analytics } from '../../../firebase';
import { useAuthContext } from '../../../auth/auth-provider';
import { useBoolean } from '../../utils/use-boolean';

interface SkillCategoryResource {
  id: string;
  title: string;
}

export const useBookmarkPage = (filterState: LibraryFilterState) => {
  const resourcesQuery = useGetBookmarkedResources();
  const bookmarkedResources = resourcesQuery.data;
  const deleteAllBookmarksConfirmationModal = useBoolean(false);
  const { locations, mediaTypes, skillCategoryIds, searchQuery } = filterState;
  const { userId } = useAuthContext();

  const filteredBookmarks = useMemo(() => {
    if (bookmarkedResources?.length === 0) return [];
    return bookmarkedResources?.filter((resource: BookmarkedResources) => {
      //search term
      const matchesSearch = !searchQuery || resource.title.toLowerCase().includes(searchQuery.toLowerCase());
      //category
      const resourceCategory = resource.skillCategory.map((sc: SkillCategoryResource) => sc.title);
      const matchesCategory =
        skillCategoryIds.length === 0 ||
        skillCategoryIds.some((selectedCategory: string) =>
          resourceCategory.includes(selectedCategory)
        );
      //type
      const matchesType = mediaTypes.length === 0 || mediaTypes.includes(resource.mediaType);
      //location
      const isResourceInternal = resource.isInternal;
      const isLocationInternal = locations?.length === 1 ? isInternalBool(locations[0]) : false;
      const matchesLocation =
        locations.length === 0 || locations.length > 1 || isResourceInternal === isLocationInternal;

      return matchesSearch && matchesCategory && matchesType && matchesLocation;
    });
  }, [bookmarkedResources, locations, mediaTypes, skillCategoryIds, searchQuery]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery && searchQuery.trim() !== '') {
        logEvent(analytics, 'search_bookmarks', {
          search_query: searchQuery,
          custom_user_id: userId,
        });
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery, userId]);

  useEffect(() => {
    if (skillCategoryIds.length > 0 || mediaTypes.length > 0 || locations.length > 0) {
      logEvent(analytics, 'filter_bookmarks', {
        category: skillCategoryIds,
        mediatype: mediaTypes,
        location: locations,
        custom_user_id: userId,
      });
    }

  }, [locations, mediaTypes, skillCategoryIds, userId]);
  return {
    resources: filteredBookmarks,
    isLoading: resourcesQuery.isLoading,
    isError: resourcesQuery.isError,
    deleteAllBookmarksConfirmationModal: deleteAllBookmarksConfirmationModal,
  };
};
